import { useMemo, useState } from "react";
import {
  spell, pcOf, diatonic, keySignature, relative, modesOnTonic,
  progressions, chordMidis, ascendingMidis, guitarInfo, capoSuggestion,
  tonicsFor, preferredTonic,
} from "@/lib/theory";
import { playMidi, playChord, playSequence } from "@/lib/audio";
import { Play, Piano, Guitar, Music2 } from "lucide-react";

const WHITE_PCS = [0, 2, 4, 5, 7, 9, 11];
const BLACK_AFTER = { 0: 1, 1: 3, 3: 6, 4: 8, 5: 10 }; // white-index -> black pc
const DISPLAY = { "C#": "C♯", "D#": "D♯", "F#": "F♯", "G#": "G♯", "A#": "A♯", "Db": "D♭", "Eb": "E♭", "Gb": "G♭", "Ab": "A♭", "Bb": "B♭" };

function Keyboard({ scalePcs, tonicPc }) {
  const whites = [];
  for (let o = 0; o < 2; o++) WHITE_PCS.forEach((pc) => whites.push({ pc, octave: 4 + o }));
  return (
    <div className="wtk-piano" data-testid="piano">
      {whites.map((w, i) => {
        const inScale = scalePcs.includes(w.pc);
        const isTonic = w.pc === tonicPc;
        const wi = i % 7;
        const blackPc = BLACK_AFTER[wi];
        return (
          <div className="wtk-wkey-wrap" key={i}>
            <button
              className={`wtk-wkey ${inScale ? "in-scale" : ""} ${isTonic ? "is-tonic" : ""}`}
              data-testid={`key-white-${i}`}
              onClick={() => playMidi(12 * (w.octave + 1) + w.pc)}
            />
            {blackPc !== undefined && i !== whites.length - 1 && (
              <button
                className={`wtk-bkey ${scalePcs.includes(blackPc) ? "in-scale" : ""} ${blackPc === tonicPc ? "is-tonic" : ""}`}
                data-testid={`key-black-${i}`}
                onClick={(e) => {
                  e.stopPropagation();
                  playMidi(12 * (w.octave + 1) + blackPc);
                }}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

function ChordDiagram({ info, name }) {
  if (!info || info.type === "none") {
    return (
      <div className="wtk-fret wtk-fret-none" data-testid="guitar-chord">
        <div className="wtk-fret-name">{name}</div>
        <div className="wtk-fret-barre">diminished — no open shape</div>
      </div>
    );
  }
  if (info.type === "barre") {
    return (
      <div className="wtk-fret wtk-fret-none" data-testid="guitar-chord">
        <div className="wtk-fret-name">{name}</div>
        <div className="wtk-fret-barre">Barre · fret {info.fret} · {info.shape}-shape</div>
      </div>
    );
  }
  return (
    <div className="wtk-fret" data-testid="guitar-chord">
      <div className="wtk-fret-name">{name}</div>
      <div className="wtk-fret-grid">
        {info.frets.map((f, s) => (
          <div className="wtk-fret-string" key={s}>
            <span className="wtk-fret-top">{f === null ? "×" : f === 0 ? "○" : ""}</span>
            <div className="wtk-fret-lines">
              {[1, 2, 3].map((fr) => (
                <span key={fr} className={`wtk-dot ${f === fr ? "on" : ""}`} />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function KeyExplorer() {
  const [tonic, setTonic] = useState("C");
  const [mode, setMode] = useState("Major");

  const info = useMemo(() => {
    const scale = spell(tonic, mode);
    return {
      scale,
      scalePcs: scale.map(pcOf),
      sig: keySignature(tonic, mode),
      rel: relative(tonic, mode),
      chords: diatonic(tonic, mode),
      modes: modesOnTonic(tonic),
      progs: progressions(tonic, mode),
      capo: capoSuggestion(tonic, mode),
    };
  }, [tonic, mode]);

  const playScale = () => playSequence(ascendingMidis(tonic, mode));
  const playChordObj = (c) => playChord(chordMidis(c.root, c.quality));
  const playProg = (chords) =>
    chords.forEach((c, i) => setTimeout(() => playChord(chordMidis(c.root, c.quality)), i * 650));
  const changeMode = (m) => { setTonic(preferredTonic(pcOf(tonic), m)); setMode(m); };

  return (
    <section className="wtk-explorer" id="explore" data-testid="key-explorer">
      <div className="wtk-explorer-head">
        <div className="wtk-eyebrow"><Music2 size={15} /> Key & scale explorer</div>
        <h1 className="wtk-title">
          Find the key to <span className="wtk-title-accent">any song.</span>
        </h1>
        <p className="wtk-sub">
          Explore scales, chords, modes and progressions — everything plays out loud.
        </p>
      </div>

      {/* Key picker */}
      <div className="wtk-keypick" data-testid="key-picker">
        <div className="wtk-tonics">
          {tonicsFor(mode).map((t) => (
            <button
              key={t}
              className={`wtk-tonic ${tonic === t ? "is-active" : ""}`}
              data-testid={`tonic-${t.replace("#", "s")}`}
              onClick={() => setTonic(t)}
            >
              {DISPLAY[t] || t}
            </button>
          ))}
        </div>
        <div className="wtk-modeswitch">
          {["Major", "Minor"].map((m) => (
            <button
              key={m}
              className={`wtk-modebtn ${mode === m ? "is-active" : ""}`}
              data-testid={`mode-${m.toLowerCase()}`}
              onClick={() => changeMode(m)}
            >
              {m}
            </button>
          ))}
        </div>
      </div>

      <div className="wtk-explorer-grid">
        {/* Selected key card */}
        <div className="wtk-panel" data-testid="selected-key-panel">
          <span className="wtk-panel-eyebrow">Selected key</span>
          <div className="wtk-bigkey" data-testid="selected-key">{tonic} {mode}</div>
          <button className="wtk-play-line" data-testid="play-scale-btn" onClick={playScale}>
            <Play size={15} /> Play scale
          </button>

          <div className="wtk-kv">
            <div><span>Scale notes</span><div className="wtk-notes" data-testid="scale-notes">
              {info.scale.map((n, i) => (
                <button key={i} className="wtk-note-chip" onClick={() => playMidi(48 + info.scalePcs[i])}>{n}</button>
              ))}
            </div></div>
            <div><span>Key signature</span><p data-testid="key-signature">{info.sig}</p></div>
            <div><span>Relative {mode === "Major" ? "minor" : "major"}</span>
              <p data-testid="relative-key">{info.rel.name}</p></div>
          </div>

          <span className="wtk-panel-eyebrow">Diatonic chords</span>
          <div className="wtk-chords" data-testid="diatonic-chords">
            {info.chords.map((c, i) => (
              <button key={i} className="wtk-chord" data-testid={`chord-${i}`} onClick={() => playChordObj(c)}>
                <span className="wtk-chord-num">{c.numeral}</span>
                <span className="wtk-chord-name">{c.name}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Progressions */}
        <div className="wtk-panel" data-testid="progressions-panel">
          <span className="wtk-panel-eyebrow">Common progressions</span>
          {info.progs.map((p, i) => (
            <div className="wtk-prog" data-testid={`progression-${i}`} key={i}>
              <div className="wtk-prog-top">
                <span className="wtk-prog-name">{p.name}</span>
                <button className="wtk-play-mini" onClick={() => playProg(p.chords)}><Play size={13} /></button>
              </div>
              <div className="wtk-prog-chords">
                {p.chords.map((c, j) => (
                  <span key={j} className="wtk-prog-chord">{c.name}{j < p.chords.length - 1 ? " →" : ""}</span>
                ))}
              </div>
            </div>
          ))}
          <span className="wtk-panel-eyebrow" style={{ marginTop: 20 }}><Guitar size={13} /> Capo helper</span>
          <p className="wtk-capo" data-testid="capo-helper">{info.capo}</p>
        </div>
      </div>

      {/* Modes */}
      <div className="wtk-modes-wrap">
        <h3 className="wtk-section-h">Modes built on {tonic}</h3>
        <div className="wtk-modes" data-testid="modes-list">
          {info.modes.map((m, i) => (
            <div className="wtk-mode" data-testid={`mode-card-${i}`} key={i}>
              <div className="wtk-mode-top">
                <span className="wtk-mode-name">{tonic} {m.name}{m.alias ? ` · ${m.alias}` : ""}</span>
                <button className="wtk-play-mini" onClick={() => playSequence(m.notes.map((n) => 48 + pcOf(n)).concat([60 + pcOf(m.notes[0])]))}>
                  <Play size={13} />
                </button>
              </div>
              <div className="wtk-mode-notes">{m.notes.join(" ")}</div>
              <p className="wtk-mode-desc">{m.desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Keyboard */}
      <div className="wtk-kbd-wrap">
        <h3 className="wtk-section-h"><Piano size={17} /> Keyboard · tap to play</h3>
        <Keyboard scalePcs={info.scalePcs} tonicPc={pcOf(tonic)} />
      </div>

      {/* Guitar voicings */}
      <div className="wtk-guitar-wrap">
        <h3 className="wtk-section-h"><Guitar size={17} /> Guitar voicings</h3>
        <div className="wtk-frets" data-testid="guitar-voicings">
          {info.chords.map((c, i) => (
            <ChordDiagram key={i} name={c.name} info={guitarInfo(c.root, c.quality)} />
          ))}
        </div>
      </div>
    </section>
  );
}
